import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { AutocompleteMultiple, useDebouncedValue } from "@msys/ui";
import { Chip, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { identity } from "lodash-es";
import React from "react";
import {
  OrganisationMembershipsFilters,
  OrganisationMembershipsSorting,
  OrganisationMembershipStatus,
} from "../../../clients/graphqlTypes.js";
import { useUserData } from "../../auth/useUserData.js";
import {
  alphabeticSortByLabel,
  memberToMemberOption,
} from "../users/helpers.js";
import { UserAvatarFragment } from "../users/UserAvatar.generated.js";
import { UserAvatar } from "../users/UserAvatar.js";
import {
  useOrganisationMemberSelect_MemberOptionsQuery,
  useOrganisationMemberSelect_SelectedMembersQuery,
  useOrganisationMemberSelect_ViewerMemberQuery,
} from "./OrganisationMemberSelect.generated.js";
import { DEFAULT_SORTING } from "./OrganisationMemberSelect.js";

export interface OrganisationMembersSelectProps extends Omit<
  React.ComponentProps<
    typeof AutocompleteMultiple<
      {
        label: string;
        value: string;
        membershipStatus: OrganisationMembershipStatus;
        userAvatar: UserAvatarFragment;
      },
      false
    >
  >,
  | "onChange"
  | "value"
  | "options"
  | "renderOption"
  | "renderInput"
  | "getOptionLabel"
> {
  value: string[];
  onChange: (value: string[]) => void;
  sorting?: Array<OrganisationMembershipsSorting>;
  filters?: OrganisationMembershipsFilters;
  // Pin the viewer (suffixed with "(me)") to the top of the options while no
  // search term is entered.
  showMeFirst?: boolean;
}

export const OrganisationMembersSelect = ({
  value,
  onChange,
  inputLabel,
  placeholder,
  required,
  disabled,
  readOnly,
  error,
  sorting = DEFAULT_SORTING,
  filters,
  filterSelectedOptions = true,
  showMeFirst = true,
  ...props
}: OrganisationMembersSelectProps) => {
  const { t } = useTranslate(["Global"]);
  const [inputValue, setInputValue] = React.useState("");
  const inputValueDebounced = useDebouncedValue(inputValue, 300);

  const viewer = useUserData().currentUser;
  // Never pin the viewer past an explicit id restriction (e.g. an
  // authors-only filter that the viewer is not part of).
  const canPinViewer =
    showMeFirst &&
    !!viewer &&
    (!filters?.ids || filters.ids.includes(viewer.id)) &&
    !filters?.excludeIds?.includes(viewer.id);

  const client = useApolloClient();
  const memberOptionsQuery = useOrganisationMemberSelect_MemberOptionsQuery({
    client,
    fetchPolicy: "network-only",
    variables: {
      sorting,
      filters,
      search: inputValueDebounced,
    },
  });
  const viewerMemberQuery = useOrganisationMemberSelect_ViewerMemberQuery({
    client,
    variables: {
      sorting: DEFAULT_SORTING,
      // the provided filters, narrowed down to the viewer
      filters: { ...filters, ids: viewer ? [viewer.id] : [] },
    },
    skip: !canPinViewer,
  });
  const viewerMember = canPinViewer
    ? getDataOrNull(
        (viewerMemberQuery.data ?? viewerMemberQuery.previousData)?.viewerMember
      )?.edges[0]?.node
    : undefined;
  const memberOptions = React.useMemo(() => {
    const options = (
      getDataOrNull(
        (memberOptionsQuery?.data ?? memberOptionsQuery?.previousData)
          ?.organisationMemberships
      )?.edges.map(edge => edge.node) ?? []
    )
      .map(memberToMemberOption)
      .sort(alphabeticSortByLabel);

    // While no search term is entered the viewer goes first.
    if (!viewerMember || inputValueDebounced) return options;
    return [
      memberToMemberOption(viewerMember),
      ...options.filter(option => option.value !== viewerMember.id),
    ];
  }, [
    memberOptionsQuery?.data,
    memberOptionsQuery?.previousData,
    viewerMember,
    inputValueDebounced,
  ]);

  const selectedMembersQuery = useOrganisationMemberSelect_SelectedMembersQuery(
    {
      client,
      variables: {
        sorting: DEFAULT_SORTING,
        selectedMembers: value,
        limit: value.length,
      },
      skip: value.length === 0,
    }
  );
  const selectedMembers = React.useMemo(
    () =>
      value.length === 0
        ? []
        : getDataOrNull(
            (selectedMembersQuery.data ?? selectedMembersQuery.previousData)
              ?.selectedMembers
          )?.edges.map(edge => edge.node),
    [selectedMembersQuery.data, selectedMembersQuery.previousData, value.length]
  );
  const selectedMemberOptions = React.useMemo(() => {
    return value.length === 0
      ? []
      : (selectedMembers?.map(memberToMemberOption) ?? []);
  }, [selectedMembers, value.length]);

  return (
    <AutocompleteMultiple
      loading={memberOptionsQuery.loading}
      inputLabel={inputLabel}
      placeholder={placeholder}
      options={memberOptions}
      value={selectedMemberOptions}
      getOptionLabel={option => option.label}
      isOptionEqualToValue={(option, value) => option.value === value.value}
      renderOption={(
        {
          // @ts-ignore
          key,
          ...props
        },
        option
      ) => (
        <li key={key} {...props}>
          <UserAvatar
            size="xs"
            userAvatar={option.userAvatar}
            style={{ marginRight: "8px" }}
          />
          {option.label}
          {viewer && option.value === viewer.id && (
            <Typography
              component="span"
              variant="body2"
              sx={{ color: "text.secondary", ml: 0.5 }}
            >
              {`(${t("me", { ns: "Global" })})`}
            </Typography>
          )}
        </li>
      )}
      required={required}
      disabled={disabled}
      readOnly={readOnly}
      renderValue={(value, getTagProps) =>
        value.map((value, index) => {
          const { key, ...props } = getTagProps({ index });
          return (
            <Chip
              key={value.value}
              avatar={<UserAvatar size="xs" userAvatar={value.userAvatar} />}
              variant="outlined"
              size="small"
              label={value.label}
              {...props}
            />
          );
        })
      }
      onInputChange={(event, newInputValue, reason) => {
        if (reason === "reset") {
          setInputValue("");
          return;
        }
        setInputValue(newInputValue);
      }}
      onChange={value => {
        onChange(value.map(v => v.value));
      }}
      error={error}
      filterSelectedOptions={filterSelectedOptions}
      filterOptions={identity}
      {...props}
    />
  );
};
